import { lazy, Suspense } from "react";
import { Outlet } from "react-router";

import AppSettings from "../../appsettings";
import DelayedSpinner from "../../components/atoms/DelayedSpinner";
import NavigationProgress from "../../components/atoms/NavigationProgress/NavigationProgress.js";
import AppBackBar from "../../components/molecules/AppBack/AppBackBar.js";
import NativeBackBridge from "../../components/molecules/AppBack/NativeBackBridge.js";
import { NavDynamicPageProvider } from "../../components/ComponentLibrary/components/NavMenu/NavDynamicPageProvider.js";
import { AppChromeOnly } from "../../components/contexts/appChromeContext";
import NativeNavigationListenerWrapper from "../../components/molecules/NativeNavigationListenerWrapper/index.js";
// Order matters: the global sheet must cascade after the chrome components'
// styles above and before the provider tree's below (e.g. shareModal.scss).
import "@scss/main.scss";
import { AppProviders } from "./AppProviders";
import { useAppShell } from "./AppShellContext";
import ScrollToTop from "./ScrollToTop.js";
import { useLinkIntentPrefetch } from "../../hooks/navigation/useLinkIntentPrefetch";

const BackToMission = lazy(
  () => import("../../components/molecules/BackToMission"),
);
const FooterComponent = lazy(
  () =>
    import("../../components/ComponentLibrary/components/Footer/FooterDynamicPage"),
);
const ScrollButton = lazy(
  () => import("../../components/molecules/scrollButton"),
);
const TopNav = lazy(() => import("../topNav/TopNav"));

const nonCriticalShellFallback = <div aria-hidden="true" />;

// Layout route for every page that has app chrome (TopNav, Footer, providers).
// Auth routes render outside it.
export default function AppShell() {
  const {
    hideContent,
    setHideContent,
    nitroLoadingState,
    nitroContextValues,
    vehiclePagePrintValue,
    windowSizeValue,
    userManagerContextValue,
    userManager,
    notificationContextValue,
    popupTooltipContextValue,
  } = useAppShell();
  useLinkIntentPrefetch();

  return (
    //  Cleanup candidate: this provider tree is deep and can be split into an AppProviders wrapper.
    <NativeNavigationListenerWrapper>
      <div className={`main ${nitroLoadingState ? "loading" : ""}`}>
        <div id="modalPortal" />
        <AppProviders
          nitroContextValues={nitroContextValues}
          vehiclePagePrintValue={vehiclePagePrintValue}
          windowSizeValue={windowSizeValue}
          userManagerContextValue={userManagerContextValue}
          userManager={userManager}
          notificationContextValue={notificationContextValue}
          popupTooltipContextValue={popupTooltipContextValue}
        >
          <div className="off-canvas-wrapper">
            <div className="off-canvas-wrapper-inner" data-off-canvas-wrapper>
              {/* NavDynamicPageProvider wraps the routed content too:
                  SearchBarV2's vehicle card reads the same DP nav
                  data via useNavDynamicPage(). */}
              <NavDynamicPageProvider>
                <AppChromeOnly>
                  <Suspense fallback={nonCriticalShellFallback}>
                    <TopNav updateHideContent={setHideContent} />
                  </Suspense>
                </AppChromeOnly>
                <div
                  className={`off-canvas-content ${hideContent ? "hidden" : ""}`}
                  data-off-canvas-content
                >
                  <div className="row fixedWidthColumn">
                    <div id={"body-content"}>
                      <NativeBackBridge />
                      <NavigationProgress />
                      <ScrollToTop />
                      {/* Inside the boundary so the bar hides while a page chunk loads. */}
                      <Suspense fallback={<DelayedSpinner />}>
                        <AppBackBar />
                        <Outlet />
                      </Suspense>
                    </div>
                    <div className="universalScroll">
                      <Suspense fallback={nonCriticalShellFallback}>
                        <ScrollButton
                          imagePath={AppSettings.AWSImgRepo.resources + "en/"}
                          url={globalThis.location.href}
                        />
                      </Suspense>
                    </div>
                    <AppChromeOnly>
                      <Suspense fallback={nonCriticalShellFallback}>
                        <BackToMission />
                      </Suspense>
                      <Suspense fallback={nonCriticalShellFallback}>
                        <FooterComponent />
                      </Suspense>
                    </AppChromeOnly>
                    <img
                      className="engage-printable-logo"
                      src={`${AppSettings.AWSImgRepo.resources}en/topNav_engage_logo.png`}
                      alt=""
                    />
                  </div>
                </div>
              </NavDynamicPageProvider>
            </div>
          </div>
        </AppProviders>
      </div>
    </NativeNavigationListenerWrapper>
  );
}
